import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  useTheme,
  alpha,
} from '@mui/material';

export interface ColInfoTableProps {
  data: Array<{
    id: string;
    label: string | React.ReactNode;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    multiline?: boolean;
    rows?: number;
  }>;
  disabled?: boolean;
  borderColor?: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
}

const ColInfoTable = ({
  data,
  disabled = false,
  borderColor,
  headerBackgroundColor,
  headerTextColor,
}: ColInfoTableProps): JSX.Element => {
  const theme = useTheme();

  // 색상 계산 (primary 계열 기준, props로 오버라이드 가능)
  const primaryColor = theme.palette.primary.main;
  const finalBorderColor = borderColor || primaryColor;
  const finalHeaderBackgroundColor =
    headerBackgroundColor || alpha(primaryColor, 0.1);
  const finalHeaderTextColor = headerTextColor || primaryColor;

  return (
    <Table
      sx={{
        width: '100%',
        border: `1px solid ${finalBorderColor}`,
        borderRadius: '4px',
        borderCollapse: 'separate',
        borderSpacing: 0,
        overflow: 'hidden',
        '& .MuiTableCell-root': {
          borderRight: `1px solid ${finalBorderColor} !important`,
          borderBottom: `1px solid ${finalBorderColor}`,
        },
        '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
          borderBottom: 'none',
        },
        '& .MuiTableCell-root:last-of-type': {
          borderRight: 'none !important',
        },
      }}
    >
      <TableHead>
        <TableRow>
          {data.map((row, index) => (
            <TableCell
              key={`header-${row.id}`}
              sx={{
                backgroundColor: finalHeaderBackgroundColor,
                padding: '1.3rem',
                textAlign: 'center',
                verticalAlign: 'middle',
                borderBottom: `1px solid ${finalBorderColor} !important`,
              }}
            >
              {typeof row.label === 'string' ? (
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: finalHeaderTextColor,
                  }}
                >
                  {row.label}
                </Typography>
              ) : (
                row.label
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          {data.map((row, index) => (
            <TableCell
              key={`input-${row.id}`}
              sx={{
                backgroundColor: 'transparent',
                padding: '1rem',
                verticalAlign: 'middle',
                ...(row.multiline === false && {
                  overflow: 'visible',
                }),
              }}
            >
              <TextField
                fullWidth
                value={row.value}
                onChange={e => row.onChange(e.target.value)}
                placeholder={row.placeholder}
                disabled={disabled}
                multiline={row.multiline !== false}
                minRows={row.rows || 5}
                maxRows={row.multiline !== false ? undefined : 5}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    border: 'none',
                    backgroundColor: '#fff',
                    overflow: 'auto',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                    fontFamily: theme.typography.subtitle1.fontFamily,
                    fontSize: theme.typography.subtitle1.fontSize,
                    fontWeight: theme.typography.subtitle1.fontWeight,
                    lineHeight: theme.typography.subtitle1.lineHeight,
                    padding: '0.5rem',
                    WebkitTextFillColor: '#000',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  },
                }}
              />
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default ColInfoTable;

